/**
 * HTTP 客户端基础类
 */

import type {
  HttpConfig,
  HttpRequest,
  HttpResponse,
  HttpInterceptor,
  HttpAdapter
} from '../../types/api'

export class HttpClient {
  private config: HttpConfig
  private adapter: HttpAdapter
  private requestInterceptors: HttpInterceptor['request'][] = []
  private responseInterceptors: HttpInterceptor['response'][] = []
  private errorInterceptors: HttpInterceptor['error'][] = []

  constructor(config: HttpConfig, adapter: HttpAdapter) {
    this.config = config
    this.adapter = adapter
  }

  /**
   * 添加请求拦截器
   */
  addRequestInterceptor(interceptor: HttpInterceptor['request']): void {
    this.requestInterceptors.push(interceptor)
  }

  /**
   * 添加响应拦截器
   */
  addResponseInterceptor(interceptor: HttpInterceptor['response']): void {
    this.responseInterceptors.push(interceptor)
  }

  /**
   * 添加错误拦截器
   */
  addErrorInterceptor(interceptor: HttpInterceptor['error']): void {
    this.errorInterceptors.push(interceptor)
  }

  /**
   * 添加前置错误拦截器。
   * 认证刷新需要先于统一登录失效处理执行，避免可恢复的 401/403 先弹登录框。
   */
  prependErrorInterceptor(interceptor: HttpInterceptor['error']): void {
    this.errorInterceptors.unshift(interceptor)
  }

  /**
   * 执行请求拦截器
   */
  private async runRequestInterceptors(request: HttpRequest): Promise<HttpRequest> {
    let modifiedRequest = request

    for (const interceptor of this.requestInterceptors) {
      if (interceptor) {
        modifiedRequest = await interceptor(modifiedRequest)
      }
    }

    return modifiedRequest
  }

  /**
   * 执行响应拦截器
   */
  private async runResponseInterceptors(response: HttpResponse): Promise<HttpResponse> {
    let modifiedResponse = response

    for (const interceptor of this.responseInterceptors) {
      if (interceptor) {
        modifiedResponse = await interceptor(modifiedResponse)
      }
    }

    return modifiedResponse
  }

  /**
   * 执行错误拦截器
   */
  private async runErrorInterceptors(error: any): Promise<any> {
    let modifiedError = error

    for (const interceptor of this.errorInterceptors) {
      if (interceptor) {
        try {
          modifiedError = await interceptor(modifiedError)
        } catch (nextError) {
          modifiedError = nextError
        }
        if (this.isHttpResponse(modifiedError)) {
          break
        }
      }
    }

    return modifiedError
  }

  private isHttpResponse(value: unknown): value is HttpResponse {
    return (
      !!value &&
      typeof value === 'object' &&
      'status' in value &&
      'data' in value &&
      'config' in value
    )
  }

  /**
   * 发送请求
   */
  async request<T = any>(request: HttpRequest): Promise<HttpResponse<T>> {
    try {
      // 合并配置
      const finalRequest: HttpRequest = {
        ...request,
        baseURL: request.baseURL || this.config.baseURL,
        timeout: request.timeout || this.config.timeout,
        headers: {
          ...this.config.headers,
          ...request.headers
        }
      }

      // 将 params 合并到 URL（适配旧版行为）
      if (finalRequest.params && Object.keys(finalRequest.params).length > 0) {
        const params = finalRequest.params
        const queryParts: string[] = []
        Object.entries(params).forEach(([key, value]) => {
          if (value === null || value === undefined) return
          queryParts.push(`${key}=${encodeURIComponent(String(value))}`)
        })
        if (queryParts.length > 0) {
          const [path, hash] = finalRequest.url.split('#')
          const separator = path.includes('?') ? '&' : '?'
          const newUrl = `${path}${separator}${queryParts.join('&')}`
          finalRequest.url = hash ? `${newUrl}#${hash}` : newUrl
        }
        // 避免后续适配器误用
        delete finalRequest.params
      }

      // 执行请求拦截器
      const interceptedRequest = await this.runRequestInterceptors(finalRequest)

      // 发送请求
      const response = await this.adapter(interceptedRequest)

      // 执行响应拦截器
      const interceptedResponse = await this.runResponseInterceptors(response)

      return interceptedResponse as HttpResponse<T>
    } catch (error) {
      // 执行错误拦截器
      const interceptedError = await this.runErrorInterceptors(error)
      if (this.isHttpResponse(interceptedError)) {
        return interceptedError as HttpResponse<T>
      }
      throw interceptedError
    }
  }

  /**
   * GET 请求
   */
  async get<T = any>(url: string, config?: Partial<HttpRequest>): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'GET'
    })
  }

  /**
   * POST 请求
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: Partial<HttpRequest>
  ): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'POST',
      data
    })
  }

  /**
   * PUT 请求
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: Partial<HttpRequest>
  ): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'PUT',
      data
    })
  }

  /**
   * DELETE 请求
   */
  async delete<T = any>(url: string, config?: Partial<HttpRequest>): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'DELETE'
    })
  }
}
